import { initializeApp, getApps } from 'firebase/app';
import { getAuth, browserLocalPersistence, setPersistence, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { getFirestore, collection, addDoc, doc, setDoc, getDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence); // Set persistence to local

const db = getFirestore(app);
const storage = getStorage(app);

export { app, auth, db, storage };

export type User = {
  uid: string;
  fullName: string;
  email: string;
  phone: string;
  dob: string;
  professionalTitle: string;
  licenseId: string;
  facilityName: string;
  community: string;
  experience: string;
  role: string;
  createdAt: Date;
};

export const registerUser = async (userData: Omit<User, 'uid' | 'createdAt'> & { password: string }) => {
  // userData must include: fullName, email, phone, dob, professionalTitle, licenseId, facilityName, community, experience, role, password
  try {
    // 1. Create Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
    const { user } = userCredential;
    // 2. Optionally update displayName
    await updateProfile(user, { displayName: userData.fullName });
    // 3. Create Firestore user document with UID as doc ID
    const userDoc: User = {
      uid: user.uid,
      fullName: userData.fullName,
      email: userData.email,
      phone: userData.phone,
      dob: userData.dob,
      professionalTitle: userData.professionalTitle,
      licenseId: userData.licenseId,
      facilityName: userData.facilityName,
      community: userData.community,
      experience: userData.experience,
      role: userData.role,
      createdAt: new Date(),
    };
    await setDoc(doc(db, 'users', user.uid), userDoc);
    return { success: true, user: userDoc };
  } catch (error) {
    // If user creation failed, nothing to clean up
    // If Firestore failed, you may want to delete the auth user (not handled here for simplicity)
    return { success: false, error };
  }
};

export const saveVolunteerApplication = async (applicationData: any) => {
  try {
    const docRef = await addDoc(collection(db, "volunteerApplications"), applicationData);
    console.log("Document written with ID: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (e) {
    console.error("Error adding document: ", e);
    return { success: false, error: e };
  }
};

export type StoryImage = { url: string; path: string };

// Admin overrides for story image slots. Doc id = slot key (e.g. "outreach-1").
// Absent slot => the page shows its static default image.
export const getStoryImages = async (): Promise<Record<string, StoryImage>> => {
  const snap = await getDocs(collection(db, 'storyImages'));
  const map: Record<string, StoryImage> = {};
  snap.docs.forEach((d) => (map[d.id] = d.data() as StoryImage));
  return map;
};

export const uploadStoryImage = async (slot: string, file: File, oldPath?: string): Promise<StoryImage> => {
  const path = `story/${slot}_${Date.now()}_${file.name}`;
  const sRef = storageRef(storage, path);
  await uploadBytes(sRef, file);
  const url = await getDownloadURL(sRef);
  await setDoc(doc(db, 'storyImages', slot), { url, path });
  if (oldPath) await deleteObject(storageRef(storage, oldPath)).catch(() => {}); // ponytail: drop replaced blob, ignore if gone
  return { url, path };
};

export const deleteStoryImage = async (slot: string, path: string): Promise<void> => {
  await deleteObject(storageRef(storage, path)).catch(() => {}); // ponytail: ignore if blob already gone
  await deleteDoc(doc(db, 'storyImages', slot));
};

export type SiteSettings = {
  contactAddress: string;
  contactEmail: string;
  contactPhones: string;
  heroImageUrl?: string;
  heroImagePath?: string;
};

// Single admin-editable settings doc. Missing fields => the site uses its hardcoded defaults.
export const getSiteSettings = async (): Promise<Partial<SiteSettings>> => {
  const snap = await getDoc(doc(db, 'siteSettings', 'main'));
  return snap.exists() ? (snap.data() as SiteSettings) : {};
};

export const saveSiteSettings = async (data: Partial<SiteSettings>): Promise<void> => {
  await setDoc(doc(db, 'siteSettings', 'main'), data, { merge: true });
};

export const uploadHeroImage = async (file: File, oldPath?: string): Promise<{ url: string; path: string }> => {
  const path = `site/hero_${Date.now()}_${file.name}`;
  const sRef = storageRef(storage, path);
  await uploadBytes(sRef, file);
  const url = await getDownloadURL(sRef);
  if (oldPath) await deleteObject(storageRef(storage, oldPath)).catch(() => {}); // ponytail: drop replaced blob
  return { url, path };
};

export const getUserProfile = async (uid: string) => {
  try {
    const userDocRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userDocRef);
    if (userSnap.exists()) {
      return userSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }
};
